import { fmtDate } from "@/lib/format";
import { useEffect, useMemo, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { setTitle } from "@/lib/pwa";
import { Eye, Lock } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FullScreenLoader } from "@/components/FullScreenLoader";
import { SourceContext, makeShareSource, shareClient, useSource } from "@/context/SourceContext";
import { apiError } from "@/lib/api";
import { cn } from "@/lib/utils";

export const SHARE_TABS = [["dashboard", "Dashboard"], ["sales", "Sales Analysis"], ["outstanding", "Outstanding"], ["expenses", "Expenses"], ["cogs", "Cost of goods sold"], ["reports", "Reports"]];

export const Message = ({ text, testid }) => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-slate-50 px-6 text-center" data-testid="share-message-screen">
    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-900"><Lock className="h-5 w-5 text-teal-300" /></div>
    <p className="max-w-md text-lg font-semibold text-slate-900" data-testid={testid}>{text}</p>
    <p className="text-xs text-slate-400">Read-only P&amp;L view · THE BRAIN</p>
  </div>
);

export const TabGate = ({ tab, children }) => (useSource().visible(tab) ? children : (
  <div className="flex h-72 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white text-sm font-medium text-slate-600" data-testid="section-not-available">This section is not available</div>
));

const shareLogo = (lid, c) => (c.has_logo ? `${process.env.REACT_APP_BACKEND_URL}/api/share/${lid}/logo?company=${c.id}&variant=light&v=${c.logo_version}` : null);

const Picker = ({ lid, info, vc, onChange }) => (
  <Select value={vc} onValueChange={onChange}>
    <SelectTrigger className="h-9 w-56 bg-white text-sm font-medium text-slate-800" data-testid="share-company-picker"><SelectValue /></SelectTrigger>
    <SelectContent>
      {info.view_mode === "combined" && <SelectItem value="all" data-testid="share-pick-all">Combined ({info.companies.length} companies)</SelectItem>}
      {info.companies.map((c) => (
        <SelectItem key={c.id} value={c.id} data-testid={`share-pick-${c.id}`}>
          <span className="flex min-w-0 items-center gap-2"><BrandMark name={c.name} logoUrl={shareLogo(lid, c)} className={c.has_logo ? "h-5 w-10" : "h-5 w-5 rounded-full text-[9px]"} testid={`share-pick-logo-${c.id}`} /><span className="truncate">{c.name}</span></span>
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export default function ShareLayout() {
  const { lid } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [nonce, setNonce] = useState(0);
  const [vc, setVc] = useState(null);
  const token = params.get("t");

  useEffect(() => {
    const client = shareClient(lid);
    (async () => {
      if (token) {
        try { await client.post("/access", { token }); setError(""); } catch (e) { setError(apiError(e)); }
        navigate(location.pathname, { replace: true });
      }
      try { setInfo((await client.get("/info")).data); } catch (e) { setInfo({ state: "missing", message: apiError(e) }); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lid, token, nonce]);

  const ok = info?.state === "ok";
  const pick = ok ? (vc && (vc === "all" ? info.view_mode === "combined" : info.companies.some((c) => c.id === vc)) ? vc : info.view_mode === "combined" ? "all" : info.companies[0].id) : null;
  const source = useMemo(() => (ok ? makeShareSource(lid, info, pick, () => setNonce((n) => n + 1)) : null), [lid, info, ok, pick]);
  if (!info) return <FullScreenLoader />;
  if (error) return <Message text={error} testid="share-access-error" />;
  if (!ok) return <Message text={info.message} testid={`share-state-${info.state}`} />;
  const tabs = SHARE_TABS.filter(([k]) => info.tabs.includes(k));
  if (!tabs.length) return <Message text="Nothing has been shared yet" testid="share-nothing" />;
  if (location.pathname.replace(/\/$/, "") === `/share/${lid}`) return <Navigate to={`/share/${lid}/${tabs[0][0]}`} replace />;
  const one = pick !== "all" ? info.companies.find((c) => c.id === pick) : null;
  const title = one ? one.name : info.label || info.companies.map((c) => c.name).join(" + ");
  const tab = location.pathname.split("/")[3];
  document.title = [title, tab && tab[0].toUpperCase() + tab.slice(1).replace(/_/g, " ")].filter(Boolean).join(" · ");
  const logoUrl = one ? shareLogo(lid, one) : null;

  return (
    <SourceContext.Provider value={source}>
      <div className="min-h-screen bg-slate-50" data-testid="share-shell">
        <header className="pt-safe sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur-md">
          <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-3 sm:px-8 lg:px-12">
            <div className="flex min-w-0 items-center gap-3">
              {one || info.companies.length === 1 ? <BrandMark name={title} logoUrl={logoUrl || shareLogo(lid, info.companies[0])} className={logoUrl || info.companies[0].has_logo ? "h-10 w-20" : "h-10 w-10"} testid="share-brand-logo" />
                : <div className="flex -space-x-2" data-testid="share-brand-logos">{info.companies.slice(0, 4).map((c) => <BrandMark key={c.id} name={c.name} logoUrl={shareLogo(lid, c)} className="h-9 w-9 rounded-full bg-white p-1 ring-2 ring-white shadow-sm" testid={`share-brand-logo-${c.id}`} />)}</div>}
              <div className="min-w-0">
                <div className="truncate font-heading text-base font-bold text-slate-900" data-testid="share-title">{title}</div>
                <div className="text-[11px] text-slate-500" data-testid="share-scope">{info.date.from ? `Data from ${fmtDate(info.date.from)} to ${fmtDate(info.date.to)}` : "All dates"}{info.note ? ` · ${info.note}` : ""}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {info.multi && <Picker lid={lid} info={info} vc={pick} onChange={setVc} />}
              <span className="flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600" data-testid="share-readonly-badge"><Eye className="h-3.5 w-3.5" /> Read-only</span>
            </div>
          </div>
          <nav className="flex gap-1 overflow-x-auto px-4 sm:px-8 lg:px-12" data-testid="share-tabs">
            {tabs.map(([k, label]) => (
              <NavLink key={k} to={`/share/${lid}/${k}`} data-testid={`share-tab-${k}`}
                className={({ isActive }) => cn("whitespace-nowrap border-b-2 px-3 py-2.5 text-sm transition-colors", isActive ? "border-teal-600 font-semibold text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900")}>{label}</NavLink>
            ))}
          </nav>
        </header>
        <main className="px-4 py-6 sm:px-8 lg:px-12 lg:py-8"><Outlet key={pick} /></main>
      </div>
    </SourceContext.Provider>
  );
}

export function LegacyView() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [msg, setMsg] = useState("");
  useEffect(() => {
    const base = `${process.env.REACT_APP_BACKEND_URL}/api/public/${slug}`;
    const opts = { method: "GET", credentials: "include" };
    const t = params.get("t");
    const req = t ? fetch(`${base}/access`, { ...opts, method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token: t }) }) : fetch(`${base}/info`, opts);
    req.then(async (r) => {
      const d = await r.json();
      if (d.lid) navigate(`/share/${d.lid}`, { replace: true });
      else setMsg(d.message || d.detail || "This link has expired or was revoked");
    }).catch(() => setMsg("This view is currently unavailable"));
  }, [slug, params, navigate]);
  return msg ? <Message text={msg} testid="legacy-view-message" /> : <FullScreenLoader />;
}
